import { Typography, FormControl, FormControlLabel, Radio, RadioGroup } from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Poll as PollIcon } from '@tunasong/icons'
import type { OneOfManyPollResponse } from '@tunasong/schemas'
import { isOneOfManyPoll, isOneOfManyPollResponse } from '@tunasong/schemas'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import type { PollProps } from '../poll.js'
import { getProgress } from '../progress-util.js'
import Progress from '../progress.js'
import styles from './vote.module.css'

export const OneOfManyPollVote: FC<PollProps> = props => {
  const { poll, userId } = props

  invariant(isOneOfManyPoll(poll), `Poll must be a OneOfManyPoll`)

  const { entities: responses = [], isSuccess } = graphHooks.useEntityChildren<OneOfManyPollResponse>({
    parentId: poll.id,
    filter: isOneOfManyPollResponse,
  })
  const userResponse = responses.find(response => response.userId === userId)

  const { createEntity, isCreatingEntity, hasCreatedEntity } = graphHooks.useEntityGraph()

  const hasVoted = isSuccess && Boolean(userResponse)

  const canVote = poll.allowChange || !hasVoted
  const readOnly = isCreatingEntity || !isSuccess || hasCreatedEntity || poll.closed || !canVote

  const handleChange = async (ev: unknown, val: string) => {
    if (!userId) {
      throw new Error(`userId must be set`)
    }
    const choice = parseInt(val, 10)
    const response = {
      type: 'pollresponse',
      pollType: 'one-of-many',
      choice,
    } satisfies OneOfManyPollResponse
    await createEntity({ parent: poll, entity: response })
  }

  const { name = 'Untitled', pollOptions = [], finalOption, closed } = poll

  const response = closed ? finalOption : userId ? (userResponse?.choice ?? null) : null
  const numResponses = Object.keys(responses).length

  return (
    <div className={styles.container}>
      <Typography variant="subtitle1" className={styles.title}>
        <PollIcon /> {name}
      </Typography>
      {poll.closed && (
        <Typography variant="caption" color="secondary">
          The Poll is closed.
        </Typography>
      )}
      <Typography variant="caption">{numResponses} participants.</Typography>

      <FormControl className={styles.formControl}>
        <RadioGroup aria-label="options" name="options" value={response ?? ''} onChange={handleChange}>
          {pollOptions.map((option, idx) => (
            <FormControlLabel
              key={idx}
              disabled={readOnly}
              value={idx}
              control={<Radio />}
              label={
                <>
                  {option.name}
                  {hasVoted ? (
                    <Progress value={getProgress(poll, responses, idx)} selected={idx === finalOption} />
                  ) : null}
                </>
              }
              className={styles.option}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </div>
  )
}
